import React, { useState } from 'react';
import { Divider, Skeleton } from '@mui/material';
import { IconChevronRight, IconUser } from '@tabler/icons-react';
import ManageGuestsDrawer from './ManageGuestsDrawer';
import GuestRow from './GuestRow';
import { TripInvite } from '../../../gql/graphql';
import { useAuth0 } from '../../../auth/AuthProvider';
import useGetUser from '../../../hooks/useGetUser';

interface Props {
  guests: TripInvite[];
  numberOfGuests: number;
  bookingId: number;
  tripId: number;
  isLeadGuest: boolean;
}

const GuestDetails: React.FC<Props> = ({
  guests,
  numberOfGuests,
  bookingId,
  tripId,
  isLeadGuest,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { user } = useAuth0();
  const { user: basicUserData, isLoading: userDataLoading } = useGetUser(
    user?.sub || '',
  );

  // ── Show a loading state if user data is still loading ──
  if (userDataLoading) {
    return <Skeleton variant="rectangular" width="100%" height={100} />;
  }

  // ── Extract magic token from the URL (if any) ──
  const searchParams = new URLSearchParams(window.location.search);
  const magicToken = searchParams.get('magic');

  // ── Filter guests based on the following logic:
  // 1. If a magic token exists, filter by matching guest.magicLink.
  // 2. Else if the current user is not the lead guest, show only one guest row (the current user's record).
  // 3. Otherwise (lead guest), show all guest invites.
  let filteredGuests: TripInvite[] = [];
  if (magicToken) {
    filteredGuests = guests.filter(guest => guest.magicLink === magicToken);
  } else if (!isLeadGuest) {
    // For non‑lead guests, filter by the current user's email.
    const currentGuest = guests.filter(
      guest => guest.email === basicUserData?.email,
    );
    filteredGuests = currentGuest.length > 0 ? [currentGuest[0]] : [];
  } else {
    filteredGuests = guests;
  }

  // ── Determine how many rows to render ──
  // For lead guest, we want to show exactly numberOfGuests rows.
  // For non‑lead guests, we display only the filtered row.
  const effectiveNumberOfRows = isLeadGuest
    ? numberOfGuests
    : filteredGuests.length;

  // ── Compute overall completion status from the filtered invites ──
  const completedGuestCount = Array.from(
    { length: effectiveNumberOfRows },
    (_, index) => filteredGuests[index],
  ).filter(guest => guest?.isCompleted).length;
  const isAllFinished =
    effectiveNumberOfRows > 0 && completedGuestCount === effectiveNumberOfRows;

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  return (
    <div className="w-full">
      {/* Mobile Section */}
      <div className="w-full md:hidden">
        <span className="text-xl font-semibold text-neutral-800">Guests</span>
        <div className="mt-4">
          <Divider />
        </div>
        <div>
          <button className="mt-4" type="button" onClick={handleDrawerOpen}>
            <div className="flex">
              <IconUser size={24} />
              <div className="flex flex-col ml-4">
                <div className="flex">
                  <span className="font-semibold text-neutral-800">
                    Guest details
                  </span>
                  <div
                    className={`flex py-1 px-2 items-center gap-2 rounded ml-2 md:ml-4 ${
                      isAllFinished
                        ? 'border border-status-success text-status-success'
                        : 'border border-status-error text-status-error'
                    }`}
                  >
                    <span className="text-sm font-semibold">
                      {isAllFinished ? 'Ready to go' : 'Action required'}
                    </span>
                  </div>
                </div>
                <div className="text-start text-neutral-500 text-sm mt-2">
                  <span>
                    {completedGuestCount} of {effectiveNumberOfRows} guests
                    ready
                  </span>
                </div>
              </div>
              <div className="flex items-center">
                <IconChevronRight size={20} />
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Desktop Section */}
      <div className="hidden md:block w-full">
        <div className="flex flex-col">
          <span className="text-xl font-semibold text-neutral-800">
            Guest details
          </span>
          <span className="text-neutral-500">
            {completedGuestCount} of {effectiveNumberOfRows} guests ready — add
            rental gear and airport transfer details
          </span>
        </div>
        {/* Render effective number of rows. For lead guest, pad with empty rows if needed */}
        {Array.from({ length: effectiveNumberOfRows }, (_, index) => {
          const guest = filteredGuests[index] ?? null;
          return (
            <GuestRow
              key={index}
              guest={guest}
              guestNumber={index + 1}
              bookingId={bookingId}
              tripId={tripId}
            />
          );
        })}
      </div>

      <ManageGuestsDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        guests={filteredGuests}
        numberOfGuests={effectiveNumberOfRows}
        bookingId={bookingId}
        tripId={tripId}
      />

      <div className="w-full md:hidden mt-4">
        <Divider />
      </div>
    </div>
  );
};

export default GuestDetails;
